/**
 * Module-level bridge between the RPC client middleware and the React-bound
 * recovery flow in `AuthGate`.
 *
 * The middleware runs outside React (it's a plain function captured at
 * module load), so it cannot call `useAuth()` to drive `removeUser()` +
 * `signinRedirect()`. `AuthGate` registers a callback here that closes
 * over the auth context; the middleware invokes the callback when an RPC
 * comes back with the `UNAUTHORIZED_PREFIX` sentinel.
 *
 * Same shape as `~/lib/access-token.ts` — a single module-level slot the
 * provider mirrors into and the middleware reads from.
 *
 * If a 401 arrives before `AuthGate` has mounted (e.g. an RPC fires during
 * the first React commit), the trigger is buffered and replayed on the
 * next `setAuthRecoveryHandler` call so the redirect still happens.
 */

let handler: (() => void) | null = null
let pendingTrigger = false

export function setAuthRecoveryHandler(next: (() => void) | null): void {
  handler = next
  if (next && pendingTrigger) {
    pendingTrigger = false
    next()
  }
}

export function triggerAuthRecovery(): void {
  if (handler) {
    handler()
  } else {
    pendingTrigger = true
  }
}

/** Test-only — reset module state between cases. */
export function resetAuthRecoveryForTests(): void {
  handler = null
  pendingTrigger = false
}

/**
 * Rolling-window rate limit for recovery redirects.
 *
 * The OIDC layer can land us in scenarios where each `signinRedirect()`
 * succeeds (Keycloak's SSO session is alive) but the freshly issued token
 * still fails — clock skew, JWKS rotation, an Access Token Lifespan
 * shorter than the lib's `expiringNotificationTimeInSeconds` (default
 * 60s), or any other reason the next renewal raises immediately. The
 * previous one-shot `sessionStorage` flag broke down here because
 * a successful sign-in cleared it, so the cycle could restart unbounded.
 *
 * This helper keeps a list of recent attempt timestamps in storage,
 * prunes anything older than `windowMs`, appends `now`, and reports
 * whether the latest attempt is still under the cap. The caller surfaces
 * a manual UI when `shouldProceed` is false instead of redirecting again.
 */
export const RECOVERY_ATTEMPTS_KEY = 'oidc.recovery_attempts'
export const RECOVERY_WINDOW_MS = 60_000
export const RECOVERY_MAX_ATTEMPTS = 3

interface RecoveryStorage {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
  removeItem(key: string): void
}

interface RecoveryRateLimitArgs {
  storage: RecoveryStorage
  now: number
  windowMs?: number
  maxAttempts?: number
}

export interface RecoveryRateLimitResult {
  shouldProceed: boolean
  attempts: number
}

export function recordRecoveryAttempt({
  storage,
  now,
  windowMs = RECOVERY_WINDOW_MS,
  maxAttempts = RECOVERY_MAX_ATTEMPTS,
}: RecoveryRateLimitArgs): RecoveryRateLimitResult {
  const cutoff = now - windowMs
  const recent = readRecent(storage, cutoff)
  if (recent.length >= maxAttempts) {
    // At or over the cap already. Don't append: that would grow the
    // persisted list unboundedly under a sustained trigger storm, and
    // would also extend the rate-limit window past the original
    // timestamps' natural expiry.
    return { shouldProceed: false, attempts: recent.length }
  }
  recent.push(now)
  storage.setItem(RECOVERY_ATTEMPTS_KEY, JSON.stringify(recent))
  return { shouldProceed: true, attempts: recent.length }
}

export function clearRecoveryAttempts(storage: RecoveryStorage): void {
  storage.removeItem(RECOVERY_ATTEMPTS_KEY)
}

/**
 * Predict whether accepting a freshly-issued OIDC token will trigger an
 * `oidc-client-ts` silent-renew storm.
 *
 * The lib schedules its `accessTokenExpiring` event at
 * `max(1s, expires_in - accessTokenExpiringNotificationTimeInSeconds)`,
 * with the notification window defaulting to 60s. Keycloak caps each
 * token's lifetime to `min(accessTokenLifespan, ssoSessionMaxLifespan -
 * timeSinceAuth)`, so as a session approaches its SSO-Session-Max
 * ceiling the IdP starts handing out tokens whose `expires_in` is below
 * that 60s window — at which point the lib re-fires renew every ~1s
 * until the session terminates. `AuthGate` calls this on every fresh
 * token; a `true` return drives `auth.stopSilentRenew()` so the existing
 * 401 → `runRecovery` path can handle the boundary instead.
 *
 * The default floor matches the lib's notification window. The
 * predicate is closed at the floor: at `expires_in === 60` the lib's
 * schedule already collapses to `max(1s, 0) = 1s`, so we stop renew
 * before the first scheduled tick rather than after.
 *
 * Pass the token's *issued* lifetime — `exp - iat` from the JWT, or the
 * `expires_in` value the IdP returned at the moment of issuance. Do
 * **not** pass `oidc-client-ts`'s `User.expires_in` getter, which is
 * dynamically computed as `expires_at - now()` and shrinks as a token
 * ages; a healthy token reloaded from storage after most of its
 * lifetime has elapsed would otherwise be mistaken for a Keycloak-
 * clamped one. `readTokenIssuedLifetime` does the canonical decode.
 */
export const SILENT_RENEW_FLOOR_SECONDS = 60

export function wouldSilentRenewStorm(
  expiresIn: number | undefined,
  floor: number = SILENT_RENEW_FLOOR_SECONDS,
): boolean {
  if (expiresIn === undefined) return false
  return expiresIn <= floor
}

/**
 * Decode an OIDC access token's JWT payload and return the issued
 * lifetime — `exp - iat`. This is the value Keycloak (or any OAuth2
 * IdP) committed to at token issuance and is stable across the token's
 * life, unlike `oidc-client-ts`'s `User.expires_in` getter which
 * subtracts the current time.
 *
 * Returns `undefined` for any parse, shape, or arithmetic failure —
 * the caller treats `undefined` the same way `wouldSilentRenewStorm`
 * does: no signal, no action.
 */
export function readTokenIssuedLifetime(
  token: string | undefined,
): number | undefined {
  if (!token) return undefined
  const parts = token.split('.')
  if (parts.length !== 3) return undefined
  let payload: unknown
  try {
    payload = JSON.parse(decodeBase64UrlUtf8(parts[1]))
  } catch {
    return undefined
  }
  if (typeof payload !== 'object' || payload === null) return undefined
  const claims = payload as { iat?: unknown; exp?: unknown }
  if (typeof claims.iat !== 'number' || typeof claims.exp !== 'number') {
    return undefined
  }
  const lifetime = claims.exp - claims.iat
  if (!Number.isFinite(lifetime) || lifetime < 0) return undefined
  return lifetime
}

function decodeBase64UrlUtf8(s: string): string {
  // Base64URL → canonical base64 (restore '+'/'/' and '=' padding) →
  // raw bytes → UTF-8 string. `atob` alone returns a Latin1 string
  // where each char is a single byte; that round-trips numeric JSON
  // claims fine but corrupts any non-ASCII string claim (Keycloak
  // names, emails) the helper might one day be asked to read.
  // Mirrors `decodeBase64Utf8` in `packages/api/src/github.ts`. The
  // `fatal: true` flag throws on invalid UTF-8 — caught by the
  // `readTokenIssuedLifetime` try/catch.
  const padding = (4 - (s.length % 4)) % 4
  const normalized = s.replaceAll('-', '+').replaceAll('_', '/') + '='.repeat(padding)
  const binary = atob(normalized)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.codePointAt(i) ?? 0
  return new TextDecoder('utf-8', { fatal: true }).decode(bytes)
}

function readRecent(storage: RecoveryStorage, cutoff: number): number[] {
  const raw = storage.getItem(RECOVERY_ATTEMPTS_KEY)
  if (!raw) return []
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return []
  }
  if (!Array.isArray(parsed)) return []
  return parsed.filter((t): t is number => Number.isFinite(t) && t >= cutoff)
}
